// New ReceptionList component : src/containers/Receptions/ReceptionList/index.tsx
import React, { useMemo } from "react";
import { Paper, Tabs, Tab } from "@mui/material";
import { Layout, TabPanel, Text } from "$components";
import useJStyles from "$hooks/useJStyles";
import useTabs from "$hooks/useTabs";
import ReceptionListTable from "./components/ReceptionListTable";
import ReceptionFilters from "./components/ReceptionFilters";
import ReceptionFormPopin from "./components/ReceptionFormPopin";
import OrderFormPopin from "./components/OrderFormPopin";
import useReceptionList from "./hooks/useReceptionList";
import useReceptionFilters from "./hooks/useReceptionFilters";
import { useCommonStyles } from "$containers/CommonStyles/CommonStyles";
import { theme } from "$utils/theme/bataillog";

function ReceptionList() {
  const { jclasses } = useJStyles();
  const { currentTab, handleChangeTab } = useTabs();
  const commonClasses = useCommonStyles();

  const receptionList: any = useReceptionList();
  const {
    receptions,
    isFetching,
    pageCount,
    pageIndex,
    filters,
    setFilter,
    setPageIndex,
    columns,
    renderRowSubComponent,
    toggleReceptionPopin,
    isSaving,
    editedReception,
    onSubmitReception,
    setShowOrderModal,
    orderNewValue,
    setOrderNewValue,
    onSubmitOrder,
    getReceptionCompany,
    showOrderModal,
    open,
    onAddReception,
    canDeleteEditedReception,
    onDeleteEditedReception,
  } = receptionList;

  const {
    dateStart_tfilter,
    dateEnd_tfilter,
    setDateStart_tfilter,
    setDateEnd_tfilter,
    onDateChange,
    synchronization_tfilter,
    setSynchronization_tfilter,
    exportReceptions,
    isExporting,
    selectedRowsReception,
    setSelectedRowsReception,
  } = useReceptionFilters(filters, setFilter);

  // Memoize the table component props to prevent unnecessary re-renders
  // Only include props that actually affect the table rendering
  const tableProps = useMemo(
    () => ({
      columns,
      receptions,
      isFetching,
      pageCount,
      filters,
      setFilter,
      setPageIndex,
      pageIndex,
      renderRowSubComponent,
      setSelectedRowsReception,
      selectedRowsReception,
    }),
    [
      columns,
      receptions,
      isFetching,
      pageCount,
      filters,
      setFilter,
      setPageIndex,
      pageIndex,
      renderRowSubComponent,
      setSelectedRowsReception,
      selectedRowsReception,
    ]
  );

  return (
    <div className={jclasses.jWrapper}>
      <Layout
        title="Liste des réceptions"
        contentClassName={jclasses.jLayoutCustom}
      >
        <Paper className={jclasses.jPaper}>
          <div>
            <Tabs
              className={commonClasses.tabHeader}
              TabIndicatorProps={{
                style: { background: theme.color.blueLight, height: 3 },
              }}
              value={currentTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleChangeTab}
              aria-label="shipments tabs"
            >
              <Tab label="Fournisseurs/reverse" />
              <Tab label="Transfert" />
            </Tabs>
          </div>
          <TabPanel value={currentTab} index={0}>
            <ReceptionFilters
              filters={filters}
              setFilter={setFilter}
              dateStart_tfilter={dateStart_tfilter}
              dateEnd_tfilter={dateEnd_tfilter}
              setDateStart_tfilter={setDateStart_tfilter}
              setDateEnd_tfilter={setDateEnd_tfilter}
              onDateChange={onDateChange}
              synchronization_tfilter={synchronization_tfilter}
              setSynchronization_tfilter={setSynchronization_tfilter}
              exportReceptions={exportReceptions}
              isExporting={isExporting}
              selectedRowsReception={selectedRowsReception}
              onAddReception={onAddReception}
            />
            <div style={{ marginTop: 8 }}>
              <Text>
                Réception(s) sélectionnée(s) :{" "}
                {selectedRowsReception?.length || 0}
              </Text>{" "}
              {selectedRowsReception?.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedRowsReception([])}
                  className={jclasses.ghostLink}
                  style={{
                    marginTop: 4,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#457DD9",
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  (Effacer la sélection)
                </button>
              )}
            </div>
            <ReceptionListTable {...tableProps} />
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <ReceptionFilters
              filters={filters}
              setFilter={setFilter}
              dateStart_tfilter={dateStart_tfilter}
              dateEnd_tfilter={dateEnd_tfilter}
              setDateStart_tfilter={setDateStart_tfilter}
              setDateEnd_tfilter={setDateEnd_tfilter}
              onDateChange={onDateChange}
              synchronization_tfilter={synchronization_tfilter}
              setSynchronization_tfilter={setSynchronization_tfilter}
              exportReceptions={exportReceptions}
              isExporting={isExporting}
              selectedRowsReception={selectedRowsReception}
              onAddReception={onAddReception}
            />
            <div style={{ marginTop: 8 }}>
              <Text>
                Réception(s) sélectionnée(s) :{" "}
                {selectedRowsReception?.length || 0}
              </Text>{" "}
              {selectedRowsReception?.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedRowsReception([])}
                  className={jclasses.ghostLink}
                  style={{
                    marginTop: 4,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "#457DD9",
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  (Effacer la sélection)
                </button>
              )}
            </div>
            <ReceptionListTable {...tableProps} />
          </TabPanel>
        </Paper>

        <ReceptionFormPopin
          isReadOnly={
            !editedReception?.id
              ? false
              : !getReceptionCompany(editedReception)?.isActive
          }
          isSaving={isSaving}
          reception={editedReception}
          onSubmit={onSubmitReception}
          onDelete={onDeleteEditedReception}
          canDelete={canDeleteEditedReception}
          onReset={() => toggleReceptionPopin(false)}
          open={open}
          togglePopin={toggleReceptionPopin}
        />

        <OrderFormPopin
          order={null}
          orderNewValue={orderNewValue}
          onSubmit={onSubmitOrder}
          onCancelOrder={() => {
            setShowOrderModal(false);
            setOrderNewValue(undefined);
          }}
          onReset={() => {
            setShowOrderModal(false);
            setOrderNewValue(undefined);
          }}
          defaultCompanyFilter={filters?.company}
          showOrderModal={showOrderModal}
          setShowOrderModal={setShowOrderModal}
          setOrderNewValue={setOrderNewValue}
        />
      </Layout>
    </div>
  );
}

export default ReceptionList;
